// filepath: src/apps/policymaker/pages/PIR/page/PIRCreateIcrDialog.jsx
import React, {useState} from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
const _awpBase = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';
function _authH() { const t = localStorage.getItem('access_token'); return t ? {Authorization: `Bearer ${t}`, 'Content-Type': 'application/json'} : {'Content-Type': 'application/json'}; }

/**
 * Dialog to create a new ICR document.
 * If pirId is provided it will be included in linked_requirements.pir_id.
 */
export default function PIRCreateIcrDialog({pirId = null}) {
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Basic fields
    const [idInput, setIdInput] = useState('');
    const [discipline, setDiscipline] = useState('SIGINT');
    const [classification, setClassification] = useState('SECRET//NOFORN');

    // Requester
    const [agency, setAgency] = useState('');
    const [office, setOffice] = useState('');
    const [poc, setPoc] = useState('');
    const [contact, setContact] = useState('');

    // Linked requirements (policy need removed; IR and PIR taken from current document)
    const [irId, setIrId] = useState('');
    const [pirIdField, setPirIdField] = useState(pirId || '');

    // Tasking
    const [summary, setSummary] = useState('');
    const [objectives, setObjectives] = useState(['']);
    const [priority, setPriority] = useState('High');
    const [justification, setJustification] = useState('');
    const [collectionStart, setCollectionStart] = useState('');
    const [collectionEnd, setCollectionEnd] = useState('');
    const [reportingFrequency, setReportingFrequency] = useState('Daily');
    const [deliveryFormat, setDeliveryFormat] = useState('Time-sensitive SIGINT summary');
    const [deadline, setDeadline] = useState('');
    const [indicators, setIndicators] = useState([{indicator_id: '', description: ''}]);

    // Constraints
    const [legalConstraints, setLegalConstraints] = useState(['']);
    const [technicalConstraints, setTechnicalConstraints] = useState(['']);

    const [statusState] = useState('active');

    async function openDialog() {
        setError(null);
        // If we have a PIR id, try to fetch its parent IR and prefill fields
        try {
            if (pirId) {
                setPirIdField(pirId);
                const snap = await fetch(`${_awpBase}/api/intelligence-requests/${encodeURIComponent(pirId)}`, {headers: _authH()});
                if (snap.ok) {
                    const data = await snap.json();
                    if (data?.parent_ir) setIrId(data.parent_ir);
                }
            }
        } catch (e) {
            // non-blocking: still open the dialog but surface an error later if needed
            console.warn('Failed to fetch PIR parent IR', e);
        }
        setOpen(true);
    }

    function closeDialog() {
        if (saving) return;
        setOpen(false);
        setError(null);
    }

    const addObjective = () => setObjectives(o => [...o, '']);
    const removeObjective = (i) => setObjectives(o => o.filter((_, idx) => idx !== i));
    const setObjective = (i, v) => setObjectives(o => o.map((val, idx) => idx === i ? v : val));

    const addIndicator = () => setIndicators(i => [...i, {indicator_id: '', description: ''}]);
    const removeIndicator = (i) => setIndicators(ind => ind.filter((_, idx) => idx !== i));
    const setIndicator = (i, field, v) => setIndicators(ind => ind.map((obj, idx) => idx === i ? {
        ...obj,
        [field]: v
    } : obj));

    const addLegal = () => setLegalConstraints(l => [...l, '']);
    const removeLegal = (i) => setLegalConstraints(l => l.filter((_, idx) => idx !== i));
    const setLegal = (i, v) => setLegalConstraints(l => l.map((val, idx) => idx === i ? v : val));

    const addTechnical = () => setTechnicalConstraints(l => [...l, '']);
    const removeTechnical = (i) => setTechnicalConstraints(l => l.filter((_, idx) => idx !== i));
    const setTechnical = (i, v) => setTechnicalConstraints(l => l.map((val, idx) => idx === i ? v : val));

    const handleCreate = async () => {
        setError(null);

        if (!summary) {
            setError('Tasking summary is required');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                // id will be set after addDoc or use provided idInput
                type: 'collection_request',
                discipline,
                classification,
                requester: {
                    agency,
                    office,
                    poc,
                    contact,
                },
                ir_id: irId || null,
                pir_id: pirIdField || null,
                tasking: {
                    summary,
                    objectives: objectives.filter(o => o && o.trim()),
                    priority,
                    justification,
                    collection_start: collectionStart || null,
                    collection_end: collectionEnd || null,
                    reporting_frequency: reportingFrequency,
                    delivery_format: deliveryFormat,
                    deadline: deadline || null,
                    indicators: indicators.filter(ind => ind.description || ind.indicator_id),
                },
                constraints: {
                    legal: legalConstraints.filter(l => l && l.trim()),
                    technical: technicalConstraints.filter(t => t && t.trim()),
                },
                status: {
                    state: statusState,
                    created: new Date().toISOString(),
                    last_updated: new Date().toISOString(),
                }
            };

            let finalId = idInput && idInput.trim() ? idInput.trim() : null;
            if (!finalId) {
                const listRes = await fetch(`${_awpBase}/api/intelligence-requests?type=ICR&limit=1000`, {headers: _authH()});
                const existing = listRes.ok ? await listRes.json() : [];
                let max = 0;
                (Array.isArray(existing) ? existing : []).forEach(d => {
                    const m = (d.id || '').match(/^ICR-(\d+)$/);
                    if (m) { const n = parseInt(m[1], 10); if (!Number.isNaN(n)) max = Math.max(max, n); }
                });
                finalId = `ICR-${max + 1}`;
            }
            await fetch(`${_awpBase}/api/intelligence-requests`, {
                method: 'POST', headers: _authH(),
                body: JSON.stringify({...payload, id: finalId, type: 'ICR'}),
            });

            setSaving(false);
            closeDialog();
        } catch (e) {
            setError(String(e?.message || e));
            setSaving(false);
        }
    };

    return (
        <>
            <Button variant={'contained'} size={'small'} onClick={openDialog}>Create Collection Request</Button>

            <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="md">
                <DialogTitle>Create Intelligence Collection Request</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} mt={1}>
                        <TextField label="Request ID (optional)" value={idInput}
                                   onChange={e => setIdInput(e.target.value)} fullWidth/>
                        <TextField select label="Discipline" value={discipline}
                                   onChange={e => setDiscipline(e.target.value)}>
                            <MenuItem value="SIGINT">SIGINT</MenuItem>
                            <MenuItem value="HUMINT">HUMINT</MenuItem>
                            <MenuItem value="IMINT">IMINT</MenuItem>
                            <MenuItem value="OSINT">OSINT</MenuItem>
                        </TextField>
                        <TextField label="Classification" value={classification}
                                   onChange={e => setClassification(e.target.value)} fullWidth/>

                        <Typography variant="subtitle2">Requester</Typography>
                        <TextField label="Agency" value={agency} onChange={e => setAgency(e.target.value)} fullWidth/>
                        <TextField label="Office" value={office} onChange={e => setOffice(e.target.value)} fullWidth/>
                        <TextField label="POC" value={poc} onChange={e => setPoc(e.target.value)} fullWidth/>
                        <TextField label="Contact" value={contact} onChange={e => setContact(e.target.value)}
                                   fullWidth/>

                        <Typography variant="h6" mt={2}>Tasking</Typography>
                        <TextField label="Summary" value={summary} onChange={e => setSummary(e.target.value)} fullWidth
                                   multiline/>

                        <Stack>
                            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                                <Typography>Objectives</Typography>
                                <IconButton size="small" onClick={addObjective}><AddIcon/></IconButton>
                            </Stack>
                            {objectives.map((o, i) => (
                                <Stack key={i} direction={'row'} spacing={1} alignItems={'center'} sx={{mt: 1}}>
                                    <TextField value={o} onChange={e => setObjective(i, e.target.value)} fullWidth/>
                                    <IconButton onClick={() => removeObjective(i)}
                                                size="small"><RemoveCircleOutlineIcon/></IconButton>
                                </Stack>
                            ))}
                        </Stack>

                        <TextField select label="Priority" value={priority} onChange={e => setPriority(e.target.value)}>
                            <MenuItem value="High">High</MenuItem>
                            <MenuItem value="Medium">Medium</MenuItem>
                            <MenuItem value="Low">Low</MenuItem>
                        </TextField>
                        <TextField label="Justification" value={justification}
                                   onChange={e => setJustification(e.target.value)} fullWidth multiline/>
                        <Stack direction={'row'} spacing={2}>
                            <TextField label="Collection start" type="date" value={collectionStart}
                                       onChange={e => setCollectionStart(e.target.value)}
                                       InputLabelProps={{shrink: true}}/>
                            <TextField label="Collection end" type="date" value={collectionEnd}
                                       onChange={e => setCollectionEnd(e.target.value)}
                                       InputLabelProps={{shrink: true}}/>
                        </Stack>
                        <TextField label="Reporting frequency" value={reportingFrequency}
                                   onChange={e => setReportingFrequency(e.target.value)} fullWidth/>
                        <TextField label="Delivery format" value={deliveryFormat}
                                   onChange={e => setDeliveryFormat(e.target.value)} fullWidth/>
                        <TextField label="Deadline" type="datetime-local" value={deadline}
                                   onChange={e => setDeadline(e.target.value)} InputLabelProps={{shrink: true}}/>

                        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                            <Typography>Indicators</Typography>
                            <IconButton size="small" onClick={addIndicator}><AddIcon/></IconButton>
                        </Stack>
                        <Stack>
                            {indicators.map((ind, i) => (
                                <Stack key={i} direction={'row'} spacing={1} alignItems={'center'} sx={{mt: 1}}>
                                    <TextField label="Indicator ID" value={ind.indicator_id}
                                               onChange={e => setIndicator(i, 'indicator_id', e.target.value)}/>
                                    <TextField label="Description" value={ind.description}
                                               onChange={e => setIndicator(i, 'description', e.target.value)}
                                               fullWidth/>
                                    <IconButton onClick={() => removeIndicator(i)}
                                                size="small"><RemoveCircleOutlineIcon/></IconButton>
                                </Stack>
                            ))}
                        </Stack>

                        <Typography variant="h5">Constraints</Typography>
                        <Stack>
                            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                                <Typography>Legal</Typography>
                                <IconButton size="small" onClick={addLegal}><AddIcon/></IconButton>
                            </Stack>
                            <Typography></Typography>
                            {legalConstraints.map((l, i) => (
                                <Stack key={i} direction={'row'} spacing={1} alignItems={'center'} sx={{mt: 1}}>
                                    <TextField value={l} onChange={e => setLegal(i, e.target.value)} fullWidth/>
                                    <IconButton onClick={() => removeLegal(i)}
                                                size="small"><RemoveCircleOutlineIcon/></IconButton>
                                </Stack>
                            ))}
                        </Stack>

                        <Stack>
                            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                                <Typography>Technical</Typography>
                                <IconButton size="small" onClick={addTechnical}><AddIcon/></IconButton>
                            </Stack>
                            {technicalConstraints.map((t, i) => (
                                <Stack key={i} direction={'row'} spacing={1} alignItems={'center'} sx={{mt: 1}}>
                                    <TextField value={t} onChange={e => setTechnical(i, e.target.value)} fullWidth/>
                                    <IconButton onClick={() => removeTechnical(i)}
                                                size="small"><RemoveCircleOutlineIcon/></IconButton>
                                </Stack>
                            ))}
                        </Stack>

                        {error && <Typography color="error">{error}</Typography>}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog} disabled={saving}>Cancel</Button>
                    <Button onClick={handleCreate} variant="contained"
                            disabled={saving}>{saving ? 'Saving…' : 'Create ICR'}</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
